import React, { useState, useEffect } from "react";
import { actionPerm } from "../../../components/authNavigation/AuthNavbar";
import Table from "../../../components/backoffice/TemplateBroker/Table";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import { sortData } from "../../../utils/sortData";
import { format } from "date-fns";
import { contractAddIcon, deleteIconDark } from "../../../assetData/icons";
import IndeterminateCheckbox from "../../../components/ui/IndeterminateCheckbox";

const TemplateBrokerConfig = (props) => {
  const {
    tableData,
    buttonConfig,
    loading,
    actionPermissions,
    setDeleteTemplate,
    setSelectedItem,
    setAddTemplate,
    action,
    syncClicked,
  } = props;
  const [tableFilter, setTableFilter] = useState(tableData);
  const [sortingType, setSortingType] = useState("desc");
  const [sortColumn, setSortColumn] = useState("template_id");

  useEffect(() => {
    setTableFilter(tableData);
  }, [tableData]);

  const handleSorting = (column, sortType, type) => {
    setSortColumn(column);

    const sortedData = sortData({
      data: tableData,
      column,
      type,
      sortType,
    });

    setTableFilter(sortedData);
    setSortingType(sortingType === "asc" ? "desc" : "asc");
  };

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "sl",
        header: () => (
          <div className="text-center">
            <div className="">SR. NO.</div>
          </div>
        ),
        cell: (info) => (
          <div className="text-center">{info.row.index + 1}.</div>
        ),
      },
      {
        accessorKey: "select",
        header: ({ table }) => {
          const allRows = table.getCoreRowModel().rows; // Get all rows across pages
          const selectableRows = allRows.filter(
            (row) => row.original.template_id,
          );
          const allSelected =
            selectableRows.length > 0 &&
            selectableRows.every((row) => row.getIsSelected());
          const someSelected = selectableRows.some((row) =>
            row.getIsSelected(),
          );

          return (
            <div className="ml-[15px] flex w-full items-center justify-center">
              <IndeterminateCheckbox
                {...{
                  checked: allSelected,
                  indeterminate: someSelected && !allSelected,
                  onChange: () => {
                    if (allSelected) {
                      selectableRows.forEach((row) =>
                        row.toggleSelected(false),
                      );
                    } else {
                      selectableRows.forEach((row) => row.toggleSelected(true));
                    }
                  },
                }}
              />
            </div>
          );
        },
        cell: ({ row, table }) => {
          const handleCheckboxChange = () => {
            const currentTemplateId = row.original.template_id;
            const allRows = table.getCoreRowModel().rows; // Get all rows across all pages

            if (!row.getIsSelected()) {
              // Checking: select all rows with template_id <= current across all pages
              allRows.forEach((r) => {
                if (
                  r.original.template_id &&
                  r.original.template_id <= currentTemplateId
                ) {
                  r.toggleSelected(true);
                }
              });
            } else {
              // Unchecking: uncheck all rows with template_id >= current across all pages
              allRows.forEach((r) => {
                if (
                  r.original.template_id &&
                  r.original.template_id >= currentTemplateId
                ) {
                  r.toggleSelected(false);
                }
              });
            }
          };

          return (
            <div className="ml-[15px] flex w-full items-center justify-center">
              <IndeterminateCheckbox
                {...{
                  checked: row.getIsSelected(),
                  onChange: handleCheckboxChange,
                }}
              />
            </div>
          );
        },
      },

      {
        accessorFn: (row) => row.template_id,
        id: "template_id",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "template_id",
                sortingType === "asc" ? "desc" : "asc",
                "number",
              );
            }}
            className="flex justify-between"
          >
            <div>Template ID</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "template_id" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "template_id" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div className=""> {info.getValue()} </div>,
      },
      {
        accessorFn: (row) => row.broker_template_id,
        id: "broker_template_id",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "broker_template_id",
                sortingType === "asc" ? "desc" : "asc",
                "number",
              );
            }}
            className="flex justify-between"
          >
            <div>Broker Template ID</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "broker_template_id" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "broker_template_id" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div className=""> {info.getValue()} </div>,
      },
      {
        accessorFn: (row) => row.vendor_or_template_name,
        id: "vendor_or_template_name",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "vendor_or_template_name",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className="flex justify-between"
          >
            <div>Vendor/Template Name</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "vendor_or_template_name" &&
                  sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "vendor_or_template_name" &&
                  sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div className=""> {info.getValue()} </div>,
      },

      {
        accessorKey: "broker_name",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "broker_name",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className="flex justify-between "
          >
            <div>Broker NAME</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "broker_name" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "broker_name" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div className=""> {info.getValue()} </div>,
      },
      {
        accessorKey: "file_format",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "file_format",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className="flex justify-between"
          >
            <div>File Format</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "file_format" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "file_format" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div className="uppercase"> {info.getValue()} </div>,
      },

      {
        accessorKey: "start_date",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "start_date",
                sortingType === "asc" ? "desc" : "asc",
                "date",
              );
            }}
            className="flex justify-between"
          >
            <div className="">Start Date</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "start_date" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "start_date" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => (
          <div className="">
            {format(new Date(info.getValue()), "dd MMM yyyy")}
          </div>
        ),
      },
      // {
      //   accessorKey: "end_date",

      //   header: () => (
      //     <div
      //       onClick={() => {
      //         handleSorting(
      //           "end_date",
      //           sortingType === "asc" ? "desc" : "asc",
      //           "date",
      //         );
      //       }}
      //       className="flex justify-between"
      //     >
      //       <div className="">End Date</div>
      //       <div className="space-y-[-10px]">
      //         <FaSortUp
      //           className={` ${
      //             sortColumn === "end_date" && sortingType === "asc"
      //               ? "opacity-100"
      //               : "opacity-50"
      //           }`}
      //         />
      //         <FaSortDown
      //           className={`${
      //             sortColumn === "end_date" && sortingType === "desc"
      //               ? "opacity-100"
      //               : "opacity-50"
      //           }`}
      //         />
      //       </div>
      //     </div>
      //   ),
      //   cell: (info) => (
      //     <div className="">
      //       {format(new Date(info.getValue()), "dd MMM yyyy")}
      //     </div>
      //   ),
      // },
      {
        accessorKey: "status",
        header: () => <div className="text-center">STATUS</div>,
        cell: (info) => (
          <div className=" text-center">
            {" "}
            {info.getValue() ? "Active" : "Inactive"}{" "}
          </div>
        ),
      },
      {
        accessorKey: "action",
        header: () => <div className="text-center">Action</div>,
        cell: (info) => <div className=" text-center"></div>,
      },
    ],
    [sortColumn, sortingType, tableData, tableFilter],
  );

  const actionConfig = [
    {
      id: "1",
      name: "Map New Template To Broker",
      image: contractAddIcon,
      actionHandler: () => setAddTemplate(true),
      disable: actionPerm(actionPermissions)?.add_perm,
    },
    {
      id: "2",
      name: "Delete Template",
      image: deleteIconDark,
      actionHandler: () => setDeleteTemplate(true),
      disable: actionPerm(actionPermissions)?.delete_perm,
    },
  ];

  return (
    <Table
      columns={columns}
      tableData={tableFilter?.length > 0 ? tableFilter : tableData}
      buttonConfig={buttonConfig}
      loading={loading}
      actionConfig={actionConfig}
      setSelectedItem={setSelectedItem}
      action={action}
      syncClicked={syncClicked}
    />
  );
};

export default TemplateBrokerConfig;
